const { omit, sumBy, isEmpty, } = require('lodash');
const { startOfDay, } = require('date-fns');

const { entries } = Object;

const types = {
  once: {
    label: '1回限り',
  },
  unlimited: {
    label: '制限なし',
  },
};
const valueTypes = {
  amount: {
    label: '割引(金額)',
    isDiscount: true,
    compute: (amount, _) => Math.min(amount, _.discountAmount),
  },
  rate: {
    label: '割引(率)',
    isDiscount: true,
    compute: (amount, _) => Math.round(amount * _.discountRate * 0.01),
  },
  gift: {
    label: 'プレゼント',
    isDiscount: false,
    compute: (amount) => amount,
  },
};

// セットオプション商品が設定されていればセット優待(対象商品とオプションの同時購入が条件)
const isSetCoupon = (coupon) => !isEmpty(coupon.setOptionProductIds);

// セット条件: 対象商品(productIds)とセットオプション商品が両方カートにあること
const isSetSatisfied = (items, coupon) => {
  return [coupon.productIds, coupon.setOptionProductIds]
    .every(productIds => items.some(_ => (productIds || []).includes(_.productId) && _.quantity > 0));
};

// 対象商品/セットオプション商品それぞれの購入数
const countItems = (items, productIds) => sumBy(items.filter(_ => (productIds || []).includes(_.productId)), _ => _.quantity);

// セット成立数: 対象商品とセットオプション商品が揃った組数(セット優待でなければ数量の制限なし)
const setCount = (items, coupon) => {
  return isSetCoupon(coupon)
    ? Math.min(countItems(items, coupon.productIds), countItems(items, coupon.setOptionProductIds))
    : Infinity;
};
const statuses = {
  initial: { label: '利用可', color: 'info', },
  used: { label: '利用済み', color: 'success', },
  disabled: { label: '無効', color: 'secondary', },
};

// セット優待の率割引は、セットが成立した組数分だけを割引対象にする(高い商品から順に充当)
const rateTargetAmount = (items, targetItems, coupon) => {
  const limit = setCount(items, coupon);
  if (limit === Infinity) return sumBy(targetItems, _ => _.product.price * _.quantity);
  return [...targetItems]
    .sort((a, b) => b.product.price - a.product.price)
    .reduce((x, _) => {
      const quantity = Math.min(_.quantity, limit - x.count);
      return { count: x.count + quantity, amount: x.amount + _.product.price * quantity };
    }, { count: 0, amount: 0 })
    .amount;
};

const computeDiscountAmount = (items, coupon) => {
  const targetItems = coupon.valueType === 'gift' ? items.filter(_ => _.isGift) : items.filter(_ => coupon.productIds.includes(_.productId));
  const amount = coupon.valueType === 'rate' ? rateTargetAmount(items, targetItems, coupon) : sumBy(targetItems, _ => _.product.price * _.quantity);
  return valueTypes[coupon.valueType].compute(amount, coupon);
};

const isShipmentFree = (items, coupon) => {
  const targetItems = items.filter(_ => coupon.productIds.includes(_.productId));
  return targetItems.length > 0 && coupon.isShipmentFree;
};

// セット優待はセット条件を満たしたときだけ有効(条件未達なら適用しない)
const isAppliableCoupon = (items, coupon) => {
  return isSetCoupon(coupon) ? isSetSatisfied(items, coupon) : true;
};

// 適用対象の優待(セット優待は条件未達を除外)
const appliableCoupons = (items, coupons = []) => {
  return coupons.filter(_ => _).filter(_ => isAppliableCoupon(items, _));
};

module.exports = {
  fields: ({ products = [], } = {}) => {
    return {
      type: {
        label: 'クーポン種別',
        type: 'select',
        options: entries(types).map(([k, v]) => ({ label: v.label, value: k })),
        validations: {
          required: v => !isEmpty(v),
        },
        initialValue: 'once',
      },
      name: {
        label: 'クーポン名',
        type: 'string',
        validations: {
          required: v => !isEmpty(v),
        },
      },
      valueType: {
        label: '割引種別',
        type: 'select',
        options: entries(valueTypes).map(([k, v]) => ({ label: v.label, value: k })),
        validations: {
          required: v => !isEmpty(v),
        },
        initialValue: 'amount',
      },
      productIds: {
        label: '商品',
        type: 'multiSelect',
        options: products.map(_ => ({ ..._, label: _.label || _.name, value: _.id })),
        validations: {
          required: v => !isEmpty(v),
        },
      },
      discountAmount: {
        label: '割引額',
        type: 'integer',
        validations: {
          greaterThanOrEqualTo0: v => v == null || v >= 0,
        },
        initialValue: null,
        hidden: _ => _.valueType !== 'amount',
      },
      discountRate: {
        label: '割引率(%)',
        type: 'integer',
        validations: {
          greaterThanOrEqualTo0: v => v == null || v >= 0,
        },
        initialValue: null,
        hidden: _ => _.valueType !== 'rate',
      },
      giftProductIds: {
        label: 'プレゼント商品',
        type: 'multiSelect',
        options: products.map(_ => ({ ..._, label: _.label || _.name, value: _.id })),
        initialValue: [],
        hidden: _ => _.valueType !== 'gift',
      },
      setOptionProductIds: {
        label: 'セットオプション商品',
        type: 'multiSelect',
        options: products.filter(_ => _.isOption || _.isPart).map(_ => ({ ..._, label: _.label || _.name, value: _.id })),
        initialValue: [],
        hint: '設定すると、商品とこのオプションを同時購入した場合のみ優待が適用されます',
      },
      isShipmentFree: {
        label: '送料無料',
        type: 'boolean',
        initialValue: false,
      },
      startDate: {
        label: '開始日',
        type: 'date',
        initialValue: null,
      },
      deadlineDate: {
        label: '終了日',
        type: 'date',
        initialValue: null,
      },
      status: {
        label: '状態',
        type: 'select',
        options: _ => entries(_.type === 'unlimited' ? omit(statuses, 'used') : statuses).map(([k, v]) => ({ label: v.label, value: k })),
        initialValue: 'initial',
      },
      disabilityMessage: {
        label: '無効メッセージ',
        type: 'text',
        hidden: _ => _.status !== 'disabled',
      },
      description: {
        label: '説明',
        type: 'richText',
      },
      note: {
        label: 'メモ',
        type: 'text',
      },
    };
  },
  computeDiscountAmount,
  isShipmentFree,
  isSetCoupon,
  isSetSatisfied,
  setCount,
  isAppliableCoupon,
  appliableCoupons,
  valueTypes,
  statuses,
  types,
};
